import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { Button, Form, Icon, Divider } from "semantic-ui-react";
import { ControlledInput } from "../../../components/ControlledForm";
import queryString from "query-string";
import { OAuthIDP } from "../../../hooks/useOAuth";
import { AuthMode, SSOButton } from "./SSOButton";
import { usePublicSSOButtons } from "../hooks/usePublicSSOButtons";

interface ILoginFormProps {
  site: string;
  message?: React.JSX.Element;
  onSubmit: (data: any) => void;
  username: {
    label: string;
    [key: string]: any;
  };
  initiateOAuth: (idp: OAuthIDP, masquerade?: string) => void;
  isLoading?: boolean;
  adminMode?: boolean;
}

export const LoginForm = ({
  site,
  message,
  onSubmit,
  username,
  isLoading,
  initiateOAuth,
  adminMode,
}: ILoginFormProps) => {
  const { control, handleSubmit } = useFormContext();
  const { redirect } = queryString.parse(window.location.search, { parseBooleans: true });
  const [showPassword, setShowPassword] = useState(false);
  const isPublicLogin = window.location.pathname.startsWith(`/${site}/public`);
  const forgotPath = isPublicLogin ? "public/forgot" : "forgot";

  const { hasSSOMicrosoft, hasAnySSO, renderPublicSSOButtons } = usePublicSSOButtons({ initiateOAuth, isLoading });
  const showDivider = (isPublicLogin && hasAnySSO) || (!isPublicLogin && hasSSOMicrosoft);

  const [masquerade, setMasquerade] = useState("");

  const renderIcon = (showPassword: boolean) => (
    <Icon
      name={showPassword ? "eye slash" : "eye"}
      link
      onClick={() => setShowPassword(!showPassword)}
      aria-label={showPassword ? "Hide password" : "Show password"}
      title={showPassword ? "Hide password" : "Show password"}
    />
  );

  function buildForgotPasswordPath() {
    if (redirect) return `/${site}/${forgotPath}?redirect=${encodeURIComponent(redirect as string)}`;
    return `/${site}/${forgotPath}`;
  }

  if (adminMode) {
    return (
      <Form>
        {message}
        <Form.Field>
          <label>User Email</label>
          <label className='tw-font-normal'>Email address of the user you want to sign in as</label>
          <input value={masquerade} onChange={(e) => setMasquerade(e.target.value)} />
        </Form.Field>
        <Divider className='-tw-mb-2 tw-w-0' />
        <div className='tw-flex tw-justify-center'>
          <SSOButton
            mode={AuthMode.Employee}
            idp={OAuthIDP.Microsoft}
            initiateOAuth={initiateOAuth}
            isLoading={isLoading}
            masquerade={masquerade || "Test+MasterAdmin@gogovapps.com"}
          />
        </div>
      </Form>
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {message}
      <Form.Field required>
        <label htmlFor='email'>{username.label}</label>
        <ControlledInput id='email' name='email' control={control} type='text' autoComplete='email' />
      </Form.Field>
      <Form.Field required className='tw-mb-2'>
        <label htmlFor='password'>Password</label>
        <ControlledInput
          type={showPassword ? "text" : "password"}
          id='password'
          name='password'
          control={control}
          icon={renderIcon(showPassword)}
          iconPosition='right'
          autoComplete='current-password'
        />
      </Form.Field>
      <div className='tw-mb-4 tw-text-center tw-text-[13px] tw-font-bold xxs:tw-text-right'>
        <Link className='tw-font-bold tw-text-blue' to={buildForgotPasswordPath()}>
          Forgot Password
        </Link>
      </div>
      <div className='tw-mb-4 tw-flex tw-justify-center'>
        <Button
          className='tw-w-[35%] tw-min-w-fit tw-px-6 tw-py-3 tw-text-lg tw-leading-[1.125]'
          type='submit'
          primary
          size='medium'
          loading={isLoading}
          disabled={isLoading}
        >
          Sign In
        </Button>
      </div>
      {isPublicLogin && (
        <div className='tw-text-md tw-p-2 tw-text-center'>
          {`New user? `}
          <Link
            className='tw-font-bold tw-text-blue'
            to={`/${site}/public/register${redirect ? "?redirect=" + encodeURIComponent(redirect as string) : ""}`}
          >
            Create an account
          </Link>
        </div>
      )}
      {showDivider && (
        <Divider horizontal className='tw-pt-2 tw-font-medium'>
          {isPublicLogin ? "OR SIGN IN WITH..." : "OR"}
        </Divider>
      )}
      {isPublicLogin ? (
        renderPublicSSOButtons()
      ) : (
        <div className='tw-my-2 tw-flex tw-flex-col tw-items-center tw-justify-center'>
          {hasSSOMicrosoft && (
            <SSOButton
              mode={AuthMode.Employee}
              idp={OAuthIDP.Microsoft}
              initiateOAuth={initiateOAuth}
              isLoading={isLoading}
            />
          )}
        </div>
      )}
    </Form>
  );
};

export default LoginForm;
